import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "antd";
import {
  CloseOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import {
  clearSubscriptionBillingBannerDismissal,
  dismissSubscriptionBillingBanner,
  isSubscriptionBillingBannerDismissed,
} from "../utils/subscriptionBillingBannerDismissal";
import { emitSubscriptionAccessUpdate } from "../utils/subscriptionAccessEvents";
import "./SubscriptionBillingBanner.css";

type NoticeSeverity = "info" | "warning" | "error";

interface SubscriptionBillingNotice {
  kind:
    | "due_soon"
    | "due_today"
    | "overdue"
    | "read_only"
    | "suspended";
  severity: NoticeSeverity;
  title: string;
  message: string;
  action_url: string | null;
  action_label: string | null;
  access_state: "normal" | "warning" | "read_only" | "suspended";
  access_enforcement_enabled: boolean;
  dismissible: boolean;
}

interface SubscriptionAccessStatus {
  company_id: number | null;
  access_state: "normal" | "warning" | "read_only" | "suspended";
  reason: string | null;
  restriction_enforced: boolean;
}

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const normalizeCompanyId = (value: unknown) => {
  const companyId = Number(value);
  return Number.isSafeInteger(companyId) && companyId > 0 ? companyId : null;
};

const SubscriptionBillingBanner: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { selectedCompany } = useSelection();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<SubscriptionBillingNotice | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const activeControllerRef = useRef<AbortController | null>(null);
  const selectedCompanyId = normalizeCompanyId(selectedCompany);
  const primaryCompanyId = normalizeCompanyId(user?.company_id || user?.company?.id);
  const isSysAdmin = Number(user?.is_superadmin) === 1;
  const companyId = isSysAdmin
    ? selectedCompanyId
    : selectedCompanyId || primaryCompanyId;

  const loadBillingStatus = useCallback(async () => {
    if (!isAuthenticated || !user?.id || !companyId) {
      setNotice(null);
      return;
    }

    activeControllerRef.current?.abort();
    const controller = new AbortController();
    activeControllerRef.current = controller;

    try {
      const response = await fetch("/api/subscription/me/billing-status", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "X-Company-Id": String(companyId),
        },
        signal: controller.signal,
      });
      if (!response.ok) {
        setNotice(null);
        return;
      }

      const payload = await response.json();
      const nextAccess = payload?.success
        ? (payload.data?.access as SubscriptionAccessStatus | null) || null
        : null;
      if (nextAccess) {
        emitSubscriptionAccessUpdate({
          company_id: companyId,
          subscription_access: nextAccess,
        });
      }
      const nextNotice = payload?.success
        ? payload.data?.notice || null
        : null;
      if (!nextNotice) {
        clearSubscriptionBillingBannerDismissal(user.id, companyId);
        setIsDismissed(false);
        setNotice(null);
        return;
      }

      if (nextNotice.dismissible === false) {
        clearSubscriptionBillingBannerDismissal(user.id, companyId);
        setIsDismissed(false);
        setNotice(nextNotice);
        return;
      }

      const dismissedInStorage =
        isSubscriptionBillingBannerDismissed(user.id, companyId);
      setIsDismissed(
        (dismissedInMemory) =>
          dismissedInMemory || dismissedInStorage,
      );
      setNotice(nextNotice);
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        // Une bannière secondaire ne doit pas interrompre le travail de
        // l'utilisateur si le contrôle de facturation est momentanément indisponible.
        setNotice(null);
      }
    }
  }, [companyId, isAuthenticated, user?.id]);

  useEffect(() => {
    setNotice(null);
    setIsDismissed(
      isAuthenticated && user?.id && companyId
        ? isSubscriptionBillingBannerDismissed(user.id, companyId)
        : false,
    );
    void loadBillingStatus();

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        void loadBillingStatus();
      }
    };
    const intervalId = window.setInterval(
      refreshWhenVisible,
      REFRESH_INTERVAL_MS,
    );
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      activeControllerRef.current?.abort();
    };
  }, [loadBillingStatus]);

  const icon = useMemo(() => {
    if (!notice) return null;
    if (notice.kind === "suspended") return <StopOutlined />;
    if (notice.kind === "read_only" || notice.kind === "overdue") {
      return <ExclamationCircleOutlined />;
    }
    if (notice.kind === "due_today") return <ClockCircleOutlined />;
    return <FileTextOutlined />;
  }, [notice]);

  if (!notice || isDismissed) return null;

  return (
    <section
      className={`subscription-billing-banner subscription-billing-banner--${notice.severity}`}
      role="alert"
      aria-live="polite"
      data-testid="subscription-billing-banner"
    >
      <span className="subscription-billing-banner__icon" aria-hidden="true">
        {icon}
      </span>
      <div className="subscription-billing-banner__content">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <div className="subscription-billing-banner__actions">
        {notice.action_url && notice.action_label && (
          <Button
            className="subscription-billing-banner__action"
            size="small"
            onClick={() => navigate(notice.action_url as string)}
          >
            {notice.action_label}
          </Button>
        )}
        {notice.dismissible !== false && (
          <Button
            className="subscription-billing-banner__close"
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label="Fermer la bannière pour cette connexion"
            onClick={() => {
              dismissSubscriptionBillingBanner(user?.id, companyId);
              setIsDismissed(true);
            }}
          />
        )}
      </div>
    </section>
  );
};

export default SubscriptionBillingBanner;
